import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  Typography,
  CircularProgress,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { RootState, useAppDispatch } from "../../store";
import { Agent, EHR, AgentType, CancellationFee } from "../../types";
import { updateAgentThunk } from "../../slices/AgentSlice";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { Colors } from "../../Colors";
import FunctionalModulesLayout from "./FunctionalModulesLayout";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import { InputStyles } from "../../styles/GeneralStyles";
import { Link } from "react-router-dom";

type FeeWindowOption = "always" | "window";

export default function Cancellation() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const agents = useSelector((state: RootState) => state.agents.agents);
  const agent = agents && agents.length > 0 ? agents[0] : null;

  const [enableCancelAppointments, setEnableCancelAppointments] =
    useState<boolean>(false);
  const [
    enableCancellationRescheduleOutreach,
    setEnableCancellationRescheduleOutreach
  ] = useState<boolean>(false);
  // No-cancellation window states
  const [enableNoCancellationWindow, setEnableNoCancellationWindow] =
    useState<boolean>(false);
  const [noCancellationWindowHours, setNoCancellationWindowHours] =
    useState<number | string>("");
  // Cancellation Fee States
  const [enableCancellationFee, setEnableCancellationFee] =
    useState<boolean>(false);
  const [feeWindowOption, setFeeWindowOption] =
    useState<FeeWindowOption>("window");
  const [feeAmount, setFeeAmount] = useState<number | string>(""); // Use string to allow empty input
  const [feeHours, setFeeHours] = useState<number | string>(""); // Use string to allow empty input
  const [policyUrl, setPolicyUrl] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (agent) {
      const feeConfig = agent.cancellationFee;
      setEnableCancelAppointments(agent.enableCancelAppointments || false);
      setEnableCancellationRescheduleOutreach(
        agent.enableCancellationRescheduleOutreach || false
      );
      setEnableCancellationFee(agent.chargeCancellationFee || false);
      setFeeWindowOption(feeConfig?.window === "Always" ? "always" : "window");
      setFeeAmount(feeConfig?.amount ? feeConfig.amount / 100 : "");
      setFeeHours(
        typeof feeConfig?.window === "number" ? feeConfig.window : ""
      );
      setPolicyUrl(feeConfig?.policyUrl ?? "");
      const windowHours = agent.noCancellationWindowHours;
      setEnableNoCancellationWindow(
        typeof windowHours === "number" && windowHours > 0
      );
      setNoCancellationWindowHours(
        typeof windowHours === "number" && windowHours > 0 ? windowHours : ""
      );
    } else {
      setEnableCancelAppointments(false);
      setEnableCancellationRescheduleOutreach(false);
      setEnableCancellationFee(false);
      setFeeWindowOption("window");
      setFeeAmount("");
      setFeeHours("");
      setPolicyUrl("");
      setEnableNoCancellationWindow(false);
      setNoCancellationWindowHours("");
    }
  }, [agent]);

  const handleSave = useCallback(async () => {
    if (!agent || !user?.token) {
      showMessage("Agent data or user session not found.", "error");
      return;
    }

    setIsLoading(true);
    try {
      let windowHoursToSave = 0;
      if (enableNoCancellationWindow) {
        const hours = parseInt(String(noCancellationWindowHours), 10);
        if (isNaN(hours) || hours <= 0) {
          showMessage(
            "Please enter a valid number of hours for the no-cancellation window.",
            "error"
          );
          setIsLoading(false);
          return;
        }
        windowHoursToSave = hours;
      }

      let feeSettings: CancellationFee | undefined = undefined;
      if (enableCancellationFee) {
        const amount = parseFloat(String(feeAmount)) * 100;
        const hours = parseInt(String(feeHours), 10);

        // Basic validation
        if (isNaN(amount) || amount <= 0) {
          showMessage("Please enter a valid cancellation fee amount.", "error");
          setIsLoading(false);
          return;
        }
        if (feeWindowOption === "window" && (isNaN(hours) || hours <= 0)) {
          showMessage(
            "Please enter a valid number of hours for the cancellation window.",
            "error"
          );
          setIsLoading(false);
          return;
        }

        feeSettings = {
          amount: amount,
          window: feeWindowOption === "always" ? "Always" : hours,
          processingFee: agent.cancellationFee?.processingFee ?? 0,
          policyUrl: policyUrl || undefined
        };
      }

      const agentProps: Partial<Agent> = {
        agentId: agent.agentId,
        enableCancelAppointments: enableCancelAppointments,
        enableCancellationRescheduleOutreach:
          enableCancellationRescheduleOutreach,
        chargeCancellationFee: enableCancellationFee,
        cancellationFee: feeSettings, // Set or unset the fee object
        noCancellationWindowHours: windowHoursToSave
      };

      await dispatch(
        updateAgentThunk({ token: user.token, agent: agentProps })
      ).unwrap();
      showMessage("Cancellation settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update cancellation settings:", error);
      showMessage("Failed to update cancellation settings.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [
    agent,
    user?.token,
    enableCancelAppointments,
    enableCancellationRescheduleOutreach,
    enableCancellationFee,
    feeWindowOption,
    feeAmount,
    feeHours,
    policyUrl,
    enableNoCancellationWindow,
    noCancellationWindowHours,
    dispatch,
    showMessage
  ]);

  if (!agent) {
    return (
      <FunctionalModulesLayout>
        <CircularProgress size={30} />
      </FunctionalModulesLayout>
    );
  }

  const showCancellationSetting = agent.ehr === EHR.webpt;

  return (
    <FunctionalModulesLayout>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Appointment Cancellation
      </Typography>
      {showCancellationSetting && (
        <Box mt={2}>
          <Box display="flex" alignItems="center" ml="-10px">
            <Checkbox
              checked={enableCancelAppointments}
              onChange={(e) => {
                setEnableCancelAppointments(e.target.checked);
              }}
              sx={{ color: Colors.grey2 }}
            />
            <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>
              Allow agent to cancel appointments
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" ml="-10px" mt={1}>
            <Checkbox
              checked={enableCancellationRescheduleOutreach}
              onChange={(e) =>
                setEnableCancellationRescheduleOutreach(e.target.checked)
              }
              sx={{ color: Colors.grey2 }}
            />
            <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>
              Text cancelled patients a reschedule link if the front office
              doesn't rebook them within a few minutes
            </Typography>
          </Box>

          {enableCancelAppointments && (
            <Box mt={2}>
              <Box display="flex" alignItems="center" ml="-10px">
                <Checkbox
                  checked={enableNoCancellationWindow}
                  onChange={(e) =>
                    setEnableNoCancellationWindow(e.target.checked)
                  }
                  sx={{ color: Colors.grey2 }}
                />
                <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>
                  Block patient cancellations within a window before the
                  appointment
                </Typography>
              </Box>

              {enableNoCancellationWindow && (
                <Box mt={1} pl="30px">
                  <Typography
                    sx={{ fontSize: "0.9rem", color: Colors.white, mb: 1 }}
                  >
                    Hours before appointment to block online cancellations
                  </Typography>
                  <TextField
                    value={noCancellationWindowHours}
                    onChange={(e) =>
                      setNoCancellationWindowHours(e.target.value)
                    }
                    placeholder="24"
                    type="number"
                    sx={{ ...InputStyles, maxWidth: "150px" }}
                  />
                </Box>
              )}

              <Box mt={2} display="flex" alignItems="center" ml="-10px">
                <Checkbox
                  checked={enableCancellationFee}
                  onChange={(e) => setEnableCancellationFee(e.target.checked)}
                  sx={{ color: Colors.grey2 }}
                />
                <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>
                  Charge cancellation fees
                </Typography>
                <Typography
                  sx={{ fontSize: "0.8rem", color: Colors.error, ml: 1 }}
                >
                  Warning: Penciled cannot charge fees unless you{" "}
                  <Link to="/dashboard/settings/payment-processing">
                    set up payment processing.
                  </Link>
                </Typography>
              </Box>

              {enableCancellationFee && (
                <Box mt={1} pl="30px">
                  <RadioGroup
                    aria-label="cancellation-fee-window"
                    name="cancellation-fee-window-group"
                    value={feeWindowOption}
                    onChange={(e) =>
                      setFeeWindowOption(e.target.value as FeeWindowOption)
                    }
                  >
                    <FormControlLabel
                      value="always"
                      control={
                        <Radio
                          sx={{
                            color: Colors.grey2,
                            "&.Mui-checked": { color: Colors.primary }
                          }}
                        />
                      }
                      label="Always charge cancellation fees"
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "0.9rem",
                          color: Colors.white
                        }
                      }}
                    />
                    <FormControlLabel
                      value="window"
                      control={
                        <Radio
                          sx={{
                            color: Colors.grey2,
                            "&.Mui-checked": { color: Colors.primary }
                          }}
                        />
                      }
                      label="Only charge cancellation fees within a specific window before an appointment"
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "0.9rem",
                          color: Colors.white
                        }
                      }}
                    />
                  </RadioGroup>

                  <Box mt={2}>
                    <Typography
                      sx={{ fontSize: "0.9rem", color: Colors.white, mb: 1 }}
                    >
                      Amount of cancellation fee
                    </Typography>
                    <TextField
                      value={feeAmount}
                      onChange={(e) => setFeeAmount(e.target.value)} // Keep as string for input flexibility
                      placeholder="75"
                      type="number"
                      sx={{ ...InputStyles, maxWidth: "150px" }}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ color: Colors.grey1, mr: 0.5 }}>
                            $
                          </Typography>
                        )
                      }}
                    />
                  </Box>

                  {feeWindowOption === "window" && (
                    <Box mt={2}>
                      <Typography
                        sx={{ fontSize: "0.9rem", color: Colors.white, mb: 1 }}
                      >
                        Hours before appointment to apply cancellation fee
                      </Typography>
                      <TextField
                        value={feeHours}
                        onChange={(e) => setFeeHours(e.target.value)} // Keep as string
                        placeholder="24"
                        type="number"
                        sx={{ ...InputStyles, maxWidth: "150px" }}
                      />
                    </Box>
                  )}

                  <Box mt={2}>
                    <Typography
                      sx={{ fontSize: "0.9rem", color: Colors.white, mb: 1 }}
                    >
                      URL link outlining the cancellation policy to provide to
                      patients:
                    </Typography>
                    <TextField
                      value={policyUrl}
                      onChange={(e) => setPolicyUrl(e.target.value)}
                      placeholder=" https://yourclinic.com/cancellation-policy"
                      type="url"
                      fullWidth
                      sx={{ ...InputStyles }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <Box mt={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PrimaryButton onClick={handleSave} disabled={isLoading}>
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Save Changes"
              )}
            </PrimaryButton>
          </Box>
        </Box>
      )}
    </FunctionalModulesLayout>
  );
}
